import {
  DirectDebitStatus,
  PaymentInitiatedBy,
  ResidentState,
} from '@pflegenavi/shared/api';

export enum DashboardCardType {
  DisabledPayment = 'DisabledPayment',
  DisabledPaymentNursingHome = 'DisabledPaymentNursingHome',
  SetUpDirectDebit = 'SetUpDirectDebit',
  ManualPaymentLowBalance = 'ManualPaymentLowBalance',
  Settlement = 'Settlement',
  Archived = 'Archived',
  None = 'None',
}

export interface DashboardCardSelectionInput {
  residentState: ResidentState | undefined;
  paymentInitiatedBy: PaymentInitiatedBy | undefined;
  directDebitStatus: DirectDebitStatus;
  hasPendingCharges: boolean;
  hasThresholdDueDate: boolean;
  isDisabledPaymentForNursingHome: boolean;
}

/**
 * Determines which info card to show on the family-member dashboard.
 *
 * Priority order:
 * 1. No resident → None
 * 2. Archived → Archived
 * 3. Awaiting settlement (deceased) → Settlement
 * 4. Payment disabled → DisabledPayment (exited + active)
 * 5. Exited (move-out) → Settlement
 * 6. Active sub-tree (DD setup, NH disabled, low balance)
 * 7. Fallback → None
 *
 * Example:
 *   getDashboardCardType({
 *     residentState: ResidentState.active,
 *     paymentInitiatedBy: PaymentInitiatedBy.Disabled,
 *     directDebitStatus: DirectDebitStatus.Inactive,
 *     hasPendingCharges: false,
 *     hasThresholdDueDate: false,
 *     isDisabledPaymentForNursingHome: false,
 *   }) // => DashboardCardType.DisabledPayment
 */
export function getDashboardCardType(
  input: DashboardCardSelectionInput
): DashboardCardType {
  const {
    residentState,
    paymentInitiatedBy,
    directDebitStatus,
    hasPendingCharges,
    hasThresholdDueDate,
    isDisabledPaymentForNursingHome,
  } = input;

  if (residentState === undefined) {
    return DashboardCardType.None;
  }

  if (residentState === ResidentState.archived) {
    return DashboardCardType.Archived;
  }

  if (residentState === ResidentState.awaitingSettlement) {
    return DashboardCardType.Settlement;
  }

  if (paymentInitiatedBy === PaymentInitiatedBy.Disabled) {
    return DashboardCardType.DisabledPayment;
  }

  if (residentState === ResidentState.exited) {
    return DashboardCardType.Settlement;
  }

  if (residentState === ResidentState.active) {
    const isManualPayment =
      paymentInitiatedBy === PaymentInitiatedBy.FamilyMember;
    const isDirectDebitInactive =
      directDebitStatus === DirectDebitStatus.Inactive;

    const showSetupDirectDebit =
      isDirectDebitInactive && !isManualPayment && !hasPendingCharges;

    if (showSetupDirectDebit) {
      return DashboardCardType.SetUpDirectDebit;
    }

    if (isDisabledPaymentForNursingHome) {
      return DashboardCardType.DisabledPaymentNursingHome;
    }

    if (isManualPayment && hasThresholdDueDate && !hasPendingCharges) {
      return DashboardCardType.ManualPaymentLowBalance;
    }
  }

  return DashboardCardType.None;
}

export enum DirectDebitCardType {
  DisabledPayment = 'DisabledPayment',
  Settlement = 'Settlement',
  Archived = 'Archived',
  DirectDebitForm = 'DirectDebitForm',
}

export interface DirectDebitCardSelectionInput {
  residentState: ResidentState | undefined;
  paymentInitiatedBy: PaymentInitiatedBy | undefined;
}

/**
 * Determines which card to show on the direct-debit page.
 *
 * Example:
 *   getDirectDebitCardType({
 *     residentState: ResidentState.exited,
 *     paymentInitiatedBy: PaymentInitiatedBy.Disabled,
 *   }) // => DirectDebitCardType.DisabledPayment
 */
export function getDirectDebitCardType(
  input: DirectDebitCardSelectionInput
): DirectDebitCardType {
  const { residentState, paymentInitiatedBy } = input;

  if (residentState === ResidentState.archived) {
    return DirectDebitCardType.Archived;
  }

  if (residentState === ResidentState.awaitingSettlement) {
    return DirectDebitCardType.Settlement;
  }

  if (paymentInitiatedBy === PaymentInitiatedBy.Disabled) {
    return DirectDebitCardType.DisabledPayment;
  }

  if (residentState === ResidentState.exited) {
    return DirectDebitCardType.Settlement;
  }

  return DirectDebitCardType.DirectDebitForm;
}
